import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserRole } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;

  login: (user: User, token: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  /** Check user có permission cụ thể. SYS_ADMIN bypass. */
  hasPermission: (code: string) => boolean;
  /** Check user có ít nhất 1 trong các permission. SYS_ADMIN bypass. */
  hasAnyPermission: (codes: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        }),

      updateUser: (updates) => {
        const current = get().user;
        if (current) {
          set({ user: { ...current, ...updates } });
        }
      },

      hasRole: (role) => {
        const user = get().user;
        return user?.roles.includes(role) ?? false;
      },

      hasAnyRole: (roles) => {
        const user = get().user;
        if (!user) return false;
        return roles.some((r) => user.roles.includes(r));
      },

      hasPermission: (code) => {
        const user = get().user;
        if (!user) return false;
        if (user.roles?.includes('SYS_ADMIN' as UserRole)) return true;
        return (user.permissions ?? []).includes(code);
      },

      hasAnyPermission: (codes) => {
        const user = get().user;
        if (!user) return false;
        if (user.roles?.includes('SYS_ADMIN' as UserRole)) return true;
        const perms = user.permissions ?? [];
        return codes.some(c => perms.includes(c));
      },
    }),
    {
      name: 'hv-taskflow-auth',
    },
  ),
);
